<template>
  <DashboardDetailLayout
    title="Responsiveness"
    subtitle="ELB latency from CloudWatch + per-endpoint timing from AuditLog."
    :sampleData="false"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1 mb-4">
      <MetricCard
        title="ELB Latency p50 (1h)"
        :value="loading ? '—' : (data.latency_p50_1h_ms != null ? `${data.latency_p50_1h_ms} ms` : '—')"
        subtitle="Median, last hour"
      />
      <MetricCard
        title="ELB Latency p95 (1h)"
        :value="loading ? '—' : (data.latency_p95_1h_ms != null ? `${data.latency_p95_1h_ms} ms` : '—')"
        subtitle="95th percentile"
      />
      <MetricCard
        title="ELB Latency p99 (1h)"
        :value="loading ? '—' : (data.latency_p99_1h_ms != null ? `${data.latency_p99_1h_ms} ms` : '—')"
        subtitle="99th percentile"
      />
      <MetricCard
        title="5xx Rate (1h)"
        :value="loading ? '—' : (data.error_5xx_rate_1h_pct != null ? `${data.error_5xx_rate_1h_pct.toFixed(2)}%` : '—')"
        subtitle="ELB 5xx / total requests"
        :valueTone="data.error_5xx_rate_1h_pct > 0.5 ? 'bad' : 'default'"
      />
      <MetricCard
        title="Alarm Status"
        :value="loading ? '—' : (data.alarm_state || 'UNKNOWN')"
        :subtitle="data.alarm_state_changed_at ? `Since ${formatTimestamp(data.alarm_state_changed_at)}` : 'ELB High Latency Alarm'"
        :valueTone="alarmTone(data.alarm_state)"
        :borderTone="alarmTone(data.alarm_state)"
      />
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          ELB Latency — Last 24 Hours (CloudWatch)
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loading">Loading…</span>
          <span v-else-if="data.cloudwatch_error" class="text-rose-600">{{ data.cloudwatch_error }}</span>
        </div>
      </div>
      <div class="h-72">
        <Line :data="latencyChartData" :options="latencyChartOptions" />
      </div>
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Top 15 Slowest Endpoints — Last 24 Hours
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loading">Loading…</span>
          <span v-else-if="data.db_error" class="text-rose-600">{{ data.db_error }}</span>
          <span v-else>App-side timing from AuditLog (≥5 calls)</span>
        </div>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-1">Endpoint</th>
            <th class="py-1 text-right">Calls</th>
            <th class="py-1 text-right">Avg ms</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && (data.top_slow_endpoints_24h || []).length === 0">
            <td colspan="3" class="py-4 text-center text-gray-500 italic">
              No timing data yet. Waiting on the duration_ms migration to populate the column.
            </td>
          </tr>
          <tr v-for="row in (data.top_slow_endpoints_24h || [])" :key="row.path" class="border-b last:border-0">
            <td class="py-1 font-mono text-xs">{{ row.path }}</td>
            <td class="py-1 text-right font-mono">{{ formatNumbersWithCommas(row.count) }}</td>
            <td class="py-1 text-right font-mono">{{ row.avg_ms.toFixed(1) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase mb-2">
        Alarm History — Last 30 Days
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-1">When</th>
            <th class="py-1">Summary</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && (data.alarm_history_30d || []).length === 0">
            <td colspan="2" class="py-4 text-center text-gray-500 italic">No state changes in the last 30 days.</td>
          </tr>
          <tr v-for="(h, i) in (data.alarm_history_30d || [])" :key="i" class="border-b last:border-0">
            <td class="py-1 text-xs text-gray-700">{{ formatTimestamp(h.timestamp) }}</td>
            <td class="py-1">{{ h.summary }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </DashboardDetailLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'vue-chartjs'
import DashboardDetailLayout from '../DashboardDetailLayout.vue'
import MetricCard from '../MetricCard.vue'
import { formatNumbersWithCommas } from '@/composables/FormatNumbers'
import { getAdminDashboardResponsiveness } from '@/services/Company'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

const store = useStore()
const loading = ref(true)
const data = ref({})

function alarmTone(state) {
  if (state === 'OK') return 'good'
  if (state === 'ALARM') return 'bad'
  if (state === 'INSUFFICIENT_DATA') return 'warn'
  return 'default'
}

function formatTimestamp(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const latencyChartData = computed(() => {
  const series = data.value.latency_24h_series || []
  const labels = series.map(p => {
    const d = new Date(p.hour_iso)
    return d.toLocaleString(undefined, { hour: 'numeric' })
  })
  return {
    labels,
    datasets: [
      { label: 'Avg', data: series.map(p => p.avg_ms), borderColor: '#3B82F6', backgroundColor: '#3B82F6', tension: 0.3 },
      { label: 'p95', data: series.map(p => p.p95_ms), borderColor: '#F59E0B', backgroundColor: '#F59E0B', tension: 0.3 },
      { label: 'p99', data: series.map(p => p.p99_ms), borderColor: '#EF4444', backgroundColor: '#EF4444', tension: 0.3 },
    ],
  }
})

const latencyChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { callback: v => `${v} ms` }, grid: { color: 'rgba(0,0,0,0.05)' } },
  },
}

async function load() {
  const token = store.state.auth.token
  if (!token) {
    loading.value = false
    return
  }
  try {
    data.value = await getAdminDashboardResponsiveness(token)
  } catch (e) {
    // graceful: cards/chart show '—'
  } finally {
    loading.value = false
  }
}

load()
</script>
